<template>
    <div class="pb-24">
        <div class="relative h-[20rem] sm:h-[40rem] z-1">
            <picture>
                <source media="(min-width:992px)" :srcset="model.media.hero_image.lg" />
                <source media="(min-width:480px)" :srcset="model.media.hero_image.md" />
                <img :src="model.media.hero_image.sm" :alt="model.title" class="w-full h-full object-cover" />
            </picture>
        </div>

        <div class="relative">
            <div
                class="z-1 absolute w-full h-screen pt-8 sm:pt-16 bg-contain lg:bg-cover bg-no-repeat mt-[-6vw] lg:mt-[-9vw] xl:mt-[-7.5vw] 2xl:mt-[-6vw] 3xl:mt-[-5.5vw]"
                style="background-position: top center"
                v-lazy-background :lazy-background="require('~/assets/images/shapes/waves/wave-1-yellow-400.png')"
            ></div>

            <div class="container relative z-2">
                <a id="main-content" class="fixed top-0" tabindex="-1"></a>

                <div class="grid grid-cols-12 base-responsive-gap mb-10">
                    {{/* Breadcrumb */}}
                    <div class="hidden lg:block col-span-12 order-1">
                        <Breadcrumb :items="breadcrumbItems" />
                    </div>

                    {{/* Title & slogan */}}
                    <div class="col-span-12 lg:col-span-8 mt-8 order-2">
                        <h1 class="display">{{ model.name }}</h1>
                        <div v-if="model.slogan" class="mt-8 md-lead">{{ model.slogan }}</div>
                    </div>

                    {{/* "Lead" content - the first 2 elements of model.components */}}
                    <div v-if="leadComponents" class="col-span-12 lg:col-span-8 order-4 lg:order-3 mt-4 lg:mt-0">
                        <EditableContentComponentGroup :components="leadComponents" has-parent-container no-overflow />
                    </div>

                    {{/* Stats card (desktop right side) */}}
                    <div class="col-span-12 lg:col-span-4 xl:-mt-36 order-3 lg:order-4 mt-6 lg:mt-0">
                        <img
                            v-if="model.media.building_image"
                            :src="model.media.building_image"
                            :alt="model.name"
                            class="mb-12 w-full sm:w-auto"
                        />
                        <div
                            v-if="
                                model.status ||
                                model.area ||
                                model.architect ||
                                model.implementation ||
                                model.details ||
                                model.opening_hours
                            "
                            class="border-l border-black pl-4 ml-4"
                        >
                            <div v-if="model.status" class="stat-title"><T t="project.status" /></div>
                            <div v-if="model.status">{{ model.status }}</div>

                            <div v-if="model.area" class="stat-title"><T t="project.area" /></div>
                            <div v-if="model.area">{{ model.area }}</div>

                            <div v-if="model.architect" class="stat-title"><T t="project.architect" /></div>
                            <div v-if="model.architect">{{ model.architect }}</div>

                            <div v-if="model.implementation" class="stat-title"><T t="project.implementation" /></div>
                            <div v-if="model.implementation">{{ model.implementation }}</div>

                            <div
                                v-if="model.details"
                                class="stat-wysiwyg-content mt-4 lg:mt-8 first:mt-0"
                                v-html="model.details"
                            ></div>

                            <div
                                v-if="model.opening_hours"
                                class="stat-wysiwyg-content mt-4 lg:mt-8 first:mt-0"
                                v-html="model.opening_hours"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <EditableContentComponentGroup :components="contentComponents" />

        <div v-if="upcomingEvents.length" class="container grid grid-cols-12 base-responsive-gap mt-16 sm:mt-20">
            <div class="mb-8 lg:mb-12 col-span-12">
                <h2 class="header-2 separator-border-top-line">
                    <T t="programs_in_varosliget.upcoming_events" />
                </h2>
            </div>
            <div class="grid grid-cols-1 card-grid-responsive-gap col-span-12 lg:col-span-10 lg:col-start-2">
                <div v-for="eventData in upcomingEvents" :key="`event-${eventData.id}`">
                    <ProgramCard
                        :title="eventData.name"
                        :date="$getFormattedEventInterval(eventData)"
                        :description="eventData.lead_text"
                        :event-types="eventData.categories_id"
                        :img="$getMediaImage(eventData.media, 'lead')"
                        :location="$getEventLocationName(eventData.location_id)"
                        :url="$getEventUrl(eventData)"
                        :ticket-url="eventData.ticket_url"
                        :registration-url="eventData.registration_url"
                        :has-series="!!eventData.series"
                    />
                </div>
                <div class="text-center lg:mt-4">
                    <Button
                        :to="localePath({ name: 'programs-in-varosliget', query: { location: location.slug } })"
                        :label="$t('programs_in_varosliget.other_events')"
                    />
                </div>
            </div>
        </div>

        <EditableContentComponentGroup :components="lastComponent" class="mt-16 sm:mt-20" />

        <section v-if="articles.length" class="mt-16 sm:mt-20">
            <div class="container grid grid-cols-12 mb-10">
                <div class="col-span-12 sm:col-span-6 lg:col-span-7">
                    <h2 class="header-2"><T t="news.recommended_news" /></h2>
                </div>
            </div>
            <CardCarousel type="article" :items="articles" />
        </section>

        <div class="absolute bottom-0 left-0 w-full bg-gradient-to-t from-gray-100" style="height: 50vh"></div>
    </div>
</template>

<script>
import CardCarousel from '~/components/Carousels/CardCarousel/CardCarousel.vue';
import ProgramCard from '~/components/Cards/ProgramCard/ProgramCard.vue';
import EditableContentComponentGroup from '~/components/Generic/EditableContentComponentGroup.vue';

export default {
    name: 'Project',
    components: {
        CardCarousel,
        ProgramCard,
        EditableContentComponentGroup,
    },
    async asyncData({ $axios, params, redirect, localePath, i18n, $dayjs, store }) {
        let model = null;
        let articles = [];
        let upcomingEvents = [];
        let location = null;

        await $axios
            .$get(`/institutions/by-slug/${params.project}`)
            .then((res) => {
                model = res;
                location = store.state.data.eventLocations.find((x) => x.id === res.location_id);
            })
            .catch((err) => {
                console.log(err);
            });

        if (!model || model.is_internal_page_disabled) {
            return await redirect(307, localePath('the-project'));
        }

        if (location) {
            const today = $dayjs().format('YYYY-MM-DD');
            await $axios
                .$get(
                    `/events?location_id=${model.location_id}&limit=3&start_date=${today}&exhibition=0&liget_walk=0&sort_by_date=-1`
                )
                .then((res) => {
                    upcomingEvents = res.data;
                })
                .catch((err) => {
                    console.log(err);
                });
        }

        await $axios
            .$get(`/news?limit=10&institution_id=${model.id}`)
            .then((res) => {
                articles = res.data;
            })
            .catch((err) => {
                console.log(err);
            });

        await i18n.generateLocalizedRouteParams(model.locale_variants, 'project');

        const breadcrumbItems = [];

        breadcrumbItems.push([i18n.t('the_project.title'), 'the-project']);
        breadcrumbItems.push([model.name, 'project']);

        const leadComponents = model.components.slice(0, 2);
        const contentComponents = model.components.slice(2, model.components.length - 1) || [];
        const lastComponent = model.components.slice(model.components.length - 1) || [];

        if (lastComponent.length && leadComponents.length && leadComponents.find((x) => x.id === lastComponent[0].id)) {
            lastComponent.pop();
        }

        return {
            breadcrumbItems,
            articles,
            upcomingEvents,
            model,
            location,
            leadComponents,
            contentComponents,
            lastComponent,
        };
    },
    head() {
        return {
            title: this.$getPageTitle(this.model.metadata),
            meta: this.$getPageMetaData(this.model.metadata),
        };
    },
};
</script>

<style>
.stat-title {
    @apply font-bold lg:text-lg uppercase mt-4 lg:mt-8 first:mt-0;
}
.stat-wysiwyg-content {
    img {
        width: unset !important;
        height: unset !important;
        max-height: 10rem;
    }
}
</style>
